import LoadingOverlay from "components/commons/loading";
import React, { createContext, useContext, useState, useCallback } from "react";

type LoadingContextType = {
  loading: boolean;
  showLoading: () => void;
  hideLoading: () => void;
  withLoading: <T>(promise: Promise<T>) => Promise<T>;
};

const LoadingContext = createContext<LoadingContextType | null>(null);

export const useLoading = () => {
  const ctx = useContext(LoadingContext);
  if (!ctx) throw new Error("useLoading must be used within LoadingProvider");
  return ctx;
};

export const LoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loadingCount, setLoadingCount] = useState(0);

  const loading = loadingCount > 0;

  const showLoading = useCallback(() => {
    setLoadingCount((c) => c + 1);
  }, []);

  const hideLoading = useCallback(() => {
    setLoadingCount((c) => Math.max(0, c - 1)); // tránh âm
  }, []);

  const withLoading = useCallback(async <T,>(promise: Promise<T>): Promise<T> => {
    try {
      showLoading();
      return await promise;
    } finally {
      hideLoading();
    }
  }, [showLoading, hideLoading]);

  return (
    <LoadingContext.Provider value={{ loading, showLoading, hideLoading, withLoading }}>
      {children}
      <LoadingOverlay open={loading} />
    </LoadingContext.Provider>
  );
};
